import React, { useState, createContext, useContext, useRef, useEffect } from 'react';
import { FluxTab, FluxTabBar, FluxTabPane, FluxTabs } from '@meltwater/flux-web-components-react';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import UsersData from './UsersData';
import WorkspaceData from './WorkspaceData';
import styles from './UsersAndWorkspaces.module.css';
import LandingPageLayout from '../../components/LandingPageLayout';

const UserDataContext = createContext();
export const UserDataProvider = ({ children }) => {
    const [userData, setUserData] = useState(null);
    const [workspaceData, setWorkspaceData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [usersData, setUsersData] = useState({
        users: [],          // Array of user objects
        workspaces: {},     // Map of workspace IDs to workspace names
        metadata: null,     // User statistics and metadata
        isLoaded: false     // Loading state indicator
    });

    // Function to update users data
    const updateUsersData = (data) => {
        setUsersData(data);
    };

    // Function to reset users data
    const resetUsersData = () => {
        setUsersData({
            users: [],
            workspaces: {},
            metadata: null,
            isLoaded: false
        });
    };

    const contextValue = {
        userData,
        setUserData,
        workspaceData,
        setWorkspaceData,
        loading,
        setLoading,
        usersData,
        updateUsersData,
        resetUsersData
    };

    return (
        <UserDataContext.Provider value={contextValue}>
            {children}
        </UserDataContext.Provider>
    );
};

export const useUserData = () => useContext(UserDataContext);

const CompanyUsers = () => {
    const [activeTab, setActiveTab] = useState(null);
    const tabsRef = useRef(null);
    
    const handleFeatureClick = (tabId) => {
        console.log("Setting active tab from feature click:", tabId);
        setActiveTab(tabId);
    };
    
    const featureCards = [
        {
            id: "usersdata",
            icon: <PersonIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Users Data",
            description: "View and export user information including roles, permissions, and workspace assigned. Useful for user auditing and management.",
            buttonText: "Go to Users Data"
        },
        {
            id: "workspacedata",
            icon: <BarChartIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Workspace Data",
            description: "View and export workspace information including content, permissions, and shared resources. Useful for workspace auditing and management.",
            buttonText: "Go to Workspace Data"
        }
        // Add more cards here as needed
    ];
    
    useEffect(() => {
        if (activeTab && tabsRef.current) {
            const allTabs = tabsRef.current.querySelectorAll('flux-tab');
            allTabs.forEach(tab => {
                tab.removeAttribute('active');
            });
            const tabElement = tabsRef.current.querySelector(`flux-tab[id="${activeTab}"]`);
            if (tabElement) {
                tabElement.setAttribute('active', 'true');
            }
        }
    }, [activeTab]);
    
    return (
        <UserDataProvider>
            <div className={styles.divStyle}>
                {!activeTab ? (
                    <LandingPageLayout
                        title="Users & Workspaces Management"
                        description="Choose one of the options below to view or export company data."
                        features={featureCards}
                        onFeatureClick={handleFeatureClick}
                        onBackClick={() => window.location.href = '/'}
                        backButtonTooltip="Home page"
                    />
                ) : (
                    <div className={styles.userContainer}>
                        <FluxTabs ref={tabsRef}>
                            <FluxTabBar 
                                onFluxActivated={(event) => {
                                    setActiveTab(event.detail.tab.id);
                                    console.log("Tab bar activated:", event.detail.tab.id);
                                }}
                            >
                                <FluxTab id="usersdata" pane="usersdata">
                                    Users Data
                                </FluxTab>
                                <FluxTab id="workspacedata" pane="workspacedata">
                                    Workspace Data
                                </FluxTab>
                            </FluxTabBar>
                            <FluxTabPane name="usersdata" active={activeTab === "usersdata"}>
                                <div className={styles.divButtonTableContainer}>
                                    <UsersData load={activeTab === "usersdata"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="workspacedata" active={activeTab === "workspacedata"}>
                                <div className={styles.divButtonTableContainer}>
                                    <WorkspaceData load={activeTab === "workspacedata"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                        </FluxTabs>
                    </div>
                )}
            </div>
        </UserDataProvider>
    );
};

export default CompanyUsers;